// salvagers-api as the pages see it: typed calls and the query keys that
// cache them. A write invalidates the keys it changes.

import { addAdminMember, adminQueries, removeAdminMember } from "@jkast90/kast-common/admin-core";
import { apiJson, apiRequest, json, publicJson, publicRequest } from "@common/api";

export interface Me {
  sub: string;
  email: string;
  groups: string[];
  admin: boolean;
}

export interface Profile {
  user_id: string;
  display_name: string;
  updated_at: number;
}

export type BannerLevel = "info" | "warning";

export interface Banner {
  text: string;
  level: BannerLevel;
  enabled: boolean;
}

export interface StoredBanner {
  banner: Banner | null;
  updated_at: number | null;
  updated_by: string | null;
}

export interface TableInfo {
  key: string;
  name: string;
}

export interface TableRows extends TableInfo {
  items: Record<string, unknown>[];
}

export const queryKeys = {
  me: ["me"] as const,
  profile: ["me", "profile"] as const,
  banner: ["banner"] as const,
  adminBanner: ["admin", "banner"] as const,
  tables: ["admin", "tables"] as const,
  table: (key: string) => ["admin", "tables", key] as const,
};

export const getMe = () => apiJson<Me>("/me");
export const getProfile = () => apiJson<Profile>("/me/profile");
export const putProfile = (displayName: string) =>
  apiJson<Profile>("/me/profile", { method: "PUT", ...json({ display_name: displayName }) });

/** Public: the front page's "keep me posted" form. `website` is the honeypot; leave it empty. */
export const signUp = async (email: string, website = "") => {
  await publicRequest("/signups", { method: "POST", ...json({ email, website }) });
};

/** Public: what every visitor sees, or null. */
export const getBanner = () => publicJson<{ banner: Banner | null }>("/banner").then((body) => body.banner);

export const admin = {
  banner: () => apiJson<StoredBanner>("/admin/banner"),
  setBanner: (banner: Banner) => apiJson<Banner>("/admin/banner", { method: "PUT", ...json(banner) }),
  clearBanner: async () => {
    await apiRequest("/admin/banner", { method: "DELETE" });
  },
  tables: () => apiJson<TableInfo[]>("/admin/tables"),
  table: (key: string) => apiJson<TableRows>(`/admin/tables/${encodeURIComponent(key)}`),
  addMember: (email: string) => addAdminMember(apiRequest, "", email),
  removeMember: (sub: string) => removeAdminMember(apiRequest, "", sub),
};

/** The deploys, lambdas, visitors (most recent first, at most 1000), members and logs, keyed under ["admin"]. */
export const adminQuery = adminQueries(apiRequest);
